import React, {
    createContext,
    useCallback,
    useMemo,
    useReducer,
    ReactNode,
    useEffect,
    useContext,
    useState,
} from 'react';
import {
    type FproDosDocFileJsonldTypedocumentdossierRead,
    type TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DOC_TYPE_REQUIRED, DocTypeEnumType } from '@europrocurement/l2d-domain';
import useDossierDocumentFilesApi, {
    type DocumentExtension,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocumentFilesApi';
import { castIriToId, getFileExtension } from '@europrocurement/l2d-utils';
import { useDossierDocumentSignContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers/DossierDocumentSignContext';
import { type DisplayedDocument } from '../modules/Dossiers/providers/types';
import { DataState } from './types';

type DossierDocumentState = TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead;
type FichierType = FproDosDocFileJsonldTypedocumentdossierRead;

// Context state action
// Dos Doc
const ADD_DOS_DOC_CTX = 'add_dos_doc_ctx';
const ADD_DOS_DOCS_CTX = 'add_dos_docs_ctx';
const INIT_DOS_DOCS_CTX = 'init_dos_docs_ctx';
const UPDATE_DOS_DOC_CTX = 'update_dos_doc_ctx';
const REMOVE_DOS_DOC_CTX = 'remove_dos_doc_ctx';
const RESET_DOS_DOCS_CTX = 'reset_dos_docs_ctx';
// Dos Doc File
const ADD_DOS_DOC_FILE_CTX = 'add_dos_doc_file_ctx';
const UPDATE_DOS_DOC_FILE_CTX = 'update_dos_doc_file_ctx';
const REMOVE_DOS_DOC_FILE_CTX = 'remove_dos_doc_file_ctx';

// Type doc constants
const TYPE_NO_TYPE = 66;
const TYPE_LIASSE = 118;
export const STATUS_VALID = 9;
export const STATUS_REFUSED = 10;
const errorStatuses: Array<number> = [STATUS_REFUSED];
const successStatuses: Array<number> = [STATUS_VALID];

type DossierDocumentProviderState = DataState & {
    dosDocs: DossierDocumentState[];
};

type Action =
    | { type: 'add_dos_doc_ctx'; payload: DossierDocumentState }
    | { type: 'add_dos_docs_ctx'; payload: DossierDocumentState[] }
    | { type: 'init_dos_docs_ctx'; payload: DossierDocumentState[] }
    | { type: 'update_dos_doc_ctx'; payload: DossierDocumentState }
    | { type: 'remove_dos_doc_ctx'; payload: number }
    | {
          type: 'update_dos_doc_file_ctx';
          payload: {
              dosDocId: number;
              fichier: FichierType;
              removeFileFromPreviousDosDoc?: boolean;
          };
      }
    | { type: 'add_dos_doc_file_ctx'; payload: { dosDocId: number; fichier: FichierType } }
    | { type: 'remove_dos_doc_file_ctx'; payload: { fichierId: number } }
    | { type: 'reset_dos_docs_ctx' };

const DossierDocumentsContext = createContext<DossierDocumentProviderState | null>(null);
const DossierDocumentsDispatchContext = createContext<React.Dispatch<Action>>(() => {});

interface DossierDocumentsProviderProps {
    autoload: boolean;
    children: ReactNode;
    dossierIds: string[] | number[]; // dossierIds sent as props in the provider
}

// Reducer actions
const dossierDocumentsReducer = (
    state: DossierDocumentProviderState,
    action: Action,
): DossierDocumentProviderState => {
    switch (action.type) {
        // DOS DOC CONTEXT ACTIONS
        case ADD_DOS_DOC_CTX: {
            return { ...state, dosDocs: [...state.dosDocs, action.payload] };
        }
        case ADD_DOS_DOCS_CTX: {
            return { ...state, dosDocs: [...state.dosDocs, ...action.payload] };
        }
        case INIT_DOS_DOCS_CTX: {
            return { ...state, loaded: true, dosDocs: action.payload };
        }
        case UPDATE_DOS_DOC_CTX: {
            return {
                ...state,
                dosDocs: state.dosDocs.map((dosDoc) =>
                    dosDoc.id === action.payload.id ? action.payload : dosDoc,
                ),
            };
        }
        case REMOVE_DOS_DOC_CTX: {
            return {
                ...state,
                dosDocs: state.dosDocs.filter((t) => t.id !== action.payload),
            };
        }

        // DOS DOC FILE CONTEXT ACTIONS
        case ADD_DOS_DOC_FILE_CTX: {
            return {
                ...state,
                dosDocs: state.dosDocs.map((dosDoc) => {
                    if (dosDoc.id === action.payload.dosDocId) {
                        const updatedFichiers = dosDoc.fichiers
                            ? [...dosDoc.fichiers, action.payload.fichier]
                            : [action.payload.fichier];
                        return { ...dosDoc, fichiers: updatedFichiers };
                    }
                    return dosDoc;
                }),
            };
        }

        case UPDATE_DOS_DOC_FILE_CTX: {
            // Update dosDocFile and dosDoc -> if the dosDocFile has a dosDoc change
            let removeFileFromPreviousDosDoc: DossierDocumentState | undefined;
            if (action.payload.removeFileFromPreviousDosDoc) {
                removeFileFromPreviousDosDoc = state.dosDocs.find((dosDocParam) =>
                    dosDocParam.fichiers?.some((file) => file.id === action.payload.fichier.id),
                );
            }

            return {
                ...state,
                dosDocs: state.dosDocs.map((dosDoc) => {
                    if (dosDoc.id === action.payload.dosDocId) {
                        // Check if the doc already has fichiers
                        // (ex change document type to another one, we move the file to another doc)
                        if (!dosDoc.fichiers || dosDoc.fichiers.length === 0) {
                            return {
                                ...dosDoc,
                                fichiers: [action.payload.fichier], // On ajoute le fichier
                            };
                        }

                        // Else update the current fichier in the fichiers array
                        const updatedFichiers = dosDoc.fichiers?.map((fichier) =>
                            fichier.id === action.payload.fichier.id
                                ? action.payload.fichier
                                : fichier,
                        );

                        return {
                            ...dosDoc,
                            fichiers: updatedFichiers,
                        };
                    }

                    // If file is moved from the original
                    if (
                        removeFileFromPreviousDosDoc &&
                        dosDoc.id === removeFileFromPreviousDosDoc.id
                    ) {
                        // the doc has files we remove the current file
                        if (dosDoc.fichiers && dosDoc.fichiers.length > 0) {
                            return {
                                ...dosDoc,
                                fichiers: dosDoc.fichiers.filter(
                                    (file) => file.id !== action.payload.fichier.id,
                                ),
                            };
                        }
                        return dosDoc;
                    }

                    return dosDoc;
                }),
            };
        }

        case REMOVE_DOS_DOC_FILE_CTX: {
            return {
                ...state,
                dosDocs: state.dosDocs.map((dosDoc) => {
                    if (
                        dosDoc.fichiers?.some((fichier) => fichier.id === action.payload.fichierId)
                    ) {
                        const updatedFichiers = dosDoc.fichiers.filter(
                            (fichier) => fichier.id !== action.payload.fichierId,
                        );
                        return {
                            ...dosDoc,
                            fichiers: updatedFichiers,
                        };
                    }
                    return dosDoc;
                }),
            };
        }

        case RESET_DOS_DOCS_CTX: {
            return { loaded: false, dosDocs: [] };
        }

        default: {
            // @ts-expect-error property type never
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
};

const sortByAddedAtAsc = (a: DisplayedDocument, b: DisplayedDocument): number => {
    if (!a.addedAt && !b.addedAt) return 0;
    if (!a.addedAt) return -1;
    if (!b.addedAt) return 1;
    return b.addedAt.getTime() - a.addedAt.getTime();
};

// Provider component
const DossierDocumentsProvider: React.FunctionComponent<DossierDocumentsProviderProps> = function ({
    autoload,
    children,
    dossierIds = [],
}) {
    const [dossierDocuments, dispatch] = useReducer(dossierDocumentsReducer, {
        loaded: false,
        dosDocs: [],
    });
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const { getDosDocs } = useDossierDocumentFilesApi();

    // Initial state value on mount
    useEffect(() => {
        if (!autoload || dossierDocuments.loaded === true || isFetching === true) {
            return;
        }

        if (dossierIds.length > 0) {
            setIsFetching(true);
            const dossierDocumentsPromises = dossierIds.map((dossierId) =>
                getDosDocs(dossierId as number),
            );
            Promise.all(dossierDocumentsPromises).then((responses) => {
                // Dispatch les données obtenues de l'API dans le state initial
                dispatch({ type: INIT_DOS_DOCS_CTX, payload: responses.flat() || [] });
                setIsFetching(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossierIds, autoload]);

    return (
        <DossierDocumentsContext.Provider value={dossierDocuments}>
            <DossierDocumentsDispatchContext.Provider value={dispatch}>
                {children}
            </DossierDocumentsDispatchContext.Provider>
        </DossierDocumentsContext.Provider>
    );
};

// Context hooks
const useDossierDocumentsCtxDispatch = () => useContext(DossierDocumentsDispatchContext);

const useDossierDocumentsCtx = () => {
    const state = useContext(DossierDocumentsContext);
    if (!state) {
        console.log('useDossierDocumentsCtx must be used within a DossierDocumentsProvider');
    }
    const dosDocsloaded = state?.loaded || false;
    const dosDocs = useMemo(() => state?.dosDocs || [], [state?.dosDocs]);
    const dispatch = useDossierDocumentsCtxDispatch();

    const { getDossiersBySignedFileId } = useDossierDocumentSignContext();
    const { getDosDocs } = useDossierDocumentFilesApi();

    const getStatusIdentifier = (statusId: number): 'pending' | 'error' | 'success' => {
        if (errorStatuses.includes(statusId)) {
            return 'error';
        }

        if (successStatuses.includes(statusId)) {
            return 'success';
        }

        return 'pending';
    };

    const toDisplayedDocument = useCallback(
        (
            document: DossierDocumentState,
            defaults: Record<string, unknown> | undefined = {},
        ): DisplayedDocument => {
            const dosDocFile = document.fichiers?.length === 1 ? document.fichiers[0] : undefined;
            const exisitingFile = dosDocFile !== undefined;
            const statusId =
                document.fichiers &&
                document.fichiers.length === 1 &&
                document.fichiers[0].statut?.id
                    ? document.fichiers[0].statut.id
                    : (document.statut?.id as number);
            const dossierId = castIriToId(document?.dossier as string);
            const statusIdentifier = exisitingFile ? getStatusIdentifier(statusId) : 'pending';
            const uploadable = !exisitingFile || statusIdentifier === 'error';

            let label = document.libelle as string;
            label = label.charAt(0).toUpperCase() + label.slice(1);
            const fileName = exisitingFile ? (dosDocFile.fichier as string) : undefined;
            const extension = fileName ? getFileExtension(fileName) : undefined;

            return {
                id: document.id as number,
                dossierId,
                fichierId: dosDocFile ? dosDocFile.id : undefined,
                type: extension as DocumentExtension,
                label,
                fileName,
                status: statusIdentifier,
                feedback: dosDocFile?.statut?.libelle ? dosDocFile.statut.libelle : undefined,
                addedAt: dosDocFile?.dateAjout ? new Date(dosDocFile?.dateAjout) : undefined,
                uploading: false,
                uploadable,
                downloadable: exisitingFile && statusIdentifier === 'success',
                showable: dosDocFile !== undefined,
                signable: dosDocFile !== undefined && extension === 'pdf',
                canDownloadSigned: exisitingFile
                    ? getDossiersBySignedFileId(dosDocFile.id as number).length > 0
                    : false,
                editable: exisitingFile && uploadable,
                removable: exisitingFile && statusIdentifier !== 'success',
                removableDoc: !!(
                    !dosDocFile &&
                    document.document?.type &&
                    document.document?.type === DOC_TYPE_REQUIRED
                ),
                movable:
                    exisitingFile &&
                    extension !== 'msg' &&
                    ['success', 'pending'].includes(statusIdentifier),
                hasType: true,
                expediteurTypeId: document?.document?.type as number,
                showStatus: true,
                documentTypeId: document?.document?.id,
                file: dosDocFile,
                ...defaults,
            };
        },
        [getDossiersBySignedFileId],
    );

    // Getter methods
    const getDossierDocuments = useCallback(
        (): DisplayedDocument[] =>
            dosDocs ? dosDocs.map((doc) => toDisplayedDocument(doc)).sort(sortByAddedAtAsc) : [],
        [dosDocs, toDisplayedDocument],
    );

    const getDossierDocumentsWithFiles = useCallback(
        (): DisplayedDocument[] =>
            dosDocs
                ? dosDocs
                      .filter((dosDoc) => dosDoc.fichiers && dosDoc.fichiers.length > 0)
                      .map((doc) => toDisplayedDocument(doc))
                      .sort(sortByAddedAtAsc)
                : [],
        [dosDocs, toDisplayedDocument],
    );

    // Get documents by category (ex: 'REQUIRED', 'DOWNLODABLE', 'INTERNAL', 'ATTACHMENT')
    const getDossierDocumentsByCategory = ({
        categoryId,
        additionalDocs = false,
        dossierId,
    }: {
        categoryId: DocTypeEnumType;
        additionalDocs?: boolean;
        dossierId?: number;
    }): DisplayedDocument[] => {
        if (!dosDocs) return [];

        const dosDocsFiltred = dosDocs.filter((doc) => {
            if (dossierId !== undefined && !doc.dossier?.endsWith(`/${dossierId}`)) return false;
            const isNoType = [TYPE_LIASSE, TYPE_NO_TYPE].includes(doc.document?.id as number);

            // check document type for main docs
            if (!additionalDocs) {
                // Type priorisé sur le type défini sur le fichier
                if (doc.fichiers && doc.fichiers.length === 1) {
                    return !isNoType && doc.fichiers[0].expediteur === categoryId;
                }
                // sinon type provenant du type de document
                return !isNoType && doc.document?.type === categoryId;
            }

            // check doc file expediteur (categoryId like 'REQUIRED', 'INTERNAL') for additional docs
            if (
                isNoType &&
                doc.fichiers &&
                doc.fichiers.length === 1 &&
                doc.fichiers[0].expediteur === categoryId
            ) {
                return true;
            }

            return false;
        });

        return dosDocsFiltred.map((doc) => toDisplayedDocument(doc)).sort(sortByAddedAtAsc);
    };

    const isDocumentInError = (fichier: FichierType): boolean => {
        const statusId = fichier?.statut?.id;

        return undefined !== statusId && getStatusIdentifier(statusId) === 'error';
    };

    const getReceivedDocumentsCountByCategory = ({
        categoryId,
    }: {
        categoryId: DocTypeEnumType;
    }): number => {
        if (!dosDocs) return 0;
        // Keep docs with file and not in error and downloadable and with a success status
        return getDossierDocumentsByCategory({ categoryId }).filter(
            (doc) =>
                doc.file &&
                !isDocumentInError(doc.file) &&
                doc.downloadable &&
                doc.status === 'success',
        ).length;
    };

    // Count missing documents for a given category (ex: 'REQUIRED', 'DOWNLODABLE', 'INTERNAL', 'ATTACHMENT')
    const getMissingDocumentsCountByCategory = ({
        categoryId,
        dossierId,
    }: {
        categoryId: DocTypeEnumType;
        dossierId?: number;
    }): number => {
        if (!dosDocs) return 0;
        // Check if the doc has linked file and if the exisiting file is not in error
        return getDossierDocumentsByCategory({ categoryId, dossierId }).filter(
            (doc) => !doc.file || isDocumentInError(doc.file),
        ).length;
    };

    const getDossierDocumentById = (id: number | undefined): DossierDocumentState | undefined => {
        if (!dosDocs || !id) return undefined;
        return dosDocs.find((doc) => doc.id === id);
    };

    const getDossierDocumentFileById = (id: number | undefined): FichierType | undefined => {
        if (!dosDocs || !id) return undefined;

        return dosDocs.flatMap((doc) => doc.fichiers || []).find((file) => file.id === id);
    };

    const refreshDossierDocumentsContext = (dossierIds: number[]) => {
        if (dossierIds.length > 0) {
            const dossierDocumentsPromises = dossierIds.map((dossierId) =>
                getDosDocs(dossierId as number),
            );
            Promise.all(dossierDocumentsPromises).then((responses) => {
                // Dispatch les données obtenues de l'API dans le state initial
                dispatch({ type: INIT_DOS_DOCS_CTX, payload: responses.flat() || [] });
            });
        } else {
            dispatch({ type: INIT_DOS_DOCS_CTX, payload: [] });
        }
    };
    return {
        dosDocs,
        dosDocsloaded,
        getDossierDocuments,
        getDossierDocumentsWithFiles,
        getDossierDocumentsByCategory,
        getMissingDocumentsCountByCategory,
        getReceivedDocumentsCountByCategory,
        getDossierDocumentById,
        // dos doc files
        getDossierDocumentFileById,
        //
        refreshDossierDocumentsContext,
        toDisplayedDocument,
        getStatusIdentifier,
    };
};

// Méthode get
export {
    sortByAddedAtAsc,
    DossierDocumentsProvider,
    DossierDocumentsContext,
    DossierDocumentsDispatchContext,
    // hooks
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
    // dispatch constants actions
    // dosDoc
    ADD_DOS_DOC_CTX,
    ADD_DOS_DOCS_CTX,
    INIT_DOS_DOCS_CTX,
    UPDATE_DOS_DOC_CTX,
    REMOVE_DOS_DOC_CTX,
    RESET_DOS_DOCS_CTX,
    // dosDocFile
    ADD_DOS_DOC_FILE_CTX,
    UPDATE_DOS_DOC_FILE_CTX,
    REMOVE_DOS_DOC_FILE_CTX,
};
